import styled from '@emotion/styled'
import {
    ApiContentBlock,
    ApiContentSection,
    ApiFullLandingPageLink,
    ApiSectionType,
} from '@storefronts/common-types'
import { useContext, useState } from 'react'
import { AnalyticsContext } from '../../context/analyticsContext'
import { SectionTreeScopeContext } from '../../context/overrideContext'
import { Constants } from '../../style/styleConstants'
import { getTypographyRoleStyle } from '../../style/typography'
import { RenderSections } from './content-sections/RenderSections'
import { scaledFontSize } from '../../style/scrollingLayout'

const TabsList = styled.div({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    overflowX: 'scroll',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    position: 'sticky',
    marginBottom: 24,
    boxShadow: 'inset 0 -1px 0 gray',
})

const Tab = styled.div<{ active: boolean }>(
    {
        marginRight: 15,
        fontSize: scaledFontSize(16),
        cursor: 'pointer',
        paddingBottom: 6,
        paddingLeft: 3,
        paddingRight: 3,
    },
    ({ active }) => {
        return {
            borderBottom: active ? '2px solid black' : '1px solid gray',
            ...getTypographyRoleStyle('tabText', {
                fontWeight: active ? 800 : 300,
            }),
            color: active ? 'black' : 'gray',
            whiteSpace: 'nowrap',
        }
    },
)

const Container = styled.div({})

const PaddedTabsList = styled(TabsList)({
    paddingLeft: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
    paddingRight: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
})

export const TabContainer: React.FC<{ content: ApiContentBlock }> = ({
    content,
}) => {
    const [selectedTab, setSelectedTab] = useState(0)
    const { Analytics } = useContext(AnalyticsContext)

    const _onTabClick = (idx: number) => {
        if (!content) return
        Analytics.TRACK_TAB_BAR_CLICK(content.tabs[idx].title)
        setSelectedTab(idx)
    }

    if (!content) return null

    return (
        <Container>
            {content.tabs.length > 1 && (
                <TabsList>
                    {content.tabs.map((tab, index) => {
                        return (
                            <Tab
                                active={index == selectedTab}
                                key={index}
                                onClick={() => _onTabClick(index)}
                            >
                                {tab.title}
                            </Tab>
                        )
                    })}
                </TabsList>
            )}
            <div>
                {content.tabs[selectedTab] && (
                    <SectionTreeScopeContext.Provider value="LANDING_PAGE">
                        <RenderSections
                            sections={content.tabs[selectedTab].sections}
                        />
                    </SectionTreeScopeContext.Provider>
                )}
            </div>
        </Container>
    )
}

export const ProductTabContainer: React.FC<{
    content: ApiContentBlock
    link: ApiFullLandingPageLink
    variantShopifyProductId?: string | null
    showBodyContent: boolean
    showReviews: boolean
    compact?: boolean
}> = ({
    content,
    link,
    variantShopifyProductId,
    showBodyContent,
    showReviews,
    compact,
}) => {
    const [selectedTab, setSelectedTab] = useState(0)
    const { Analytics } = useContext(AnalyticsContext)

    const _onTabClick = (idx: number) => {
        if (!content) return
        Analytics.TRACK_TAB_BAR_CLICK(content.tabs[idx].title)
        setSelectedTab(idx)
    }

    const withVirtualProductVariantContext = (
        sections: ApiContentSection[],
    ): ApiContentSection[] =>
        sections.map((s) =>
            s.type === ApiSectionType.AI_REVIEWS_SUMMARY ||
            s.type === ApiSectionType.REVIEWS_IMAGES
                ? {
                      ...s,
                      variantShopifyProductId:
                          variantShopifyProductId ?? undefined,
                  }
                : s,
        )

    let maybeSectionsWithMaybeReviews = showBodyContent
        ? withVirtualProductVariantContext(content.tabs[selectedTab].sections)
        : []
    if (showReviews) {
        maybeSectionsWithMaybeReviews = [
            ...maybeSectionsWithMaybeReviews,
            {
                type: ApiSectionType.REVIEWS_SECTION,
                linkHash: link.linkHash,
                variantShopifyProductId: variantShopifyProductId ?? undefined,
            },
        ]
    }

    if (!content) return null

    return (
        <Container>
            {showBodyContent && content.tabs.length > 1 && (
                <PaddedTabsList>
                    {content.tabs.map((tab, index) => {
                        return (
                            <Tab
                                active={index == selectedTab}
                                key={index}
                                onClick={() => _onTabClick(index)}
                            >
                                {tab.title}
                            </Tab>
                        )
                    })}
                </PaddedTabsList>
            )}
            <div>
                <SectionTreeScopeContext.Provider value="PDP">
                    <RenderSections
                        sections={maybeSectionsWithMaybeReviews}
                        compact={compact}
                        reviewsProductLink={link}
                        nested
                    />
                </SectionTreeScopeContext.Provider>
            </div>
        </Container>
    )
}
